import { FormField, FormSection } from "../dtos";

/**
 * §Conditions / §Live-gates: a LIVE client-side mirror of the engine's precondition evaluation,
 * judged against `preconditionFacts` (the case-fact operands the server ships with the step)
 * overlaid with the unsaved draft — so choosing "Individual" reveals its dependent fields in the
 * same render, no round-trip. The server stays authoritative (its sets gate saves/completion and
 * reconcile on refetch); this mirror only decides what the form RENDERS between saves.
 *
 * Fidelity contract: an element whose rule uses anything this mirror does NOT implement (review
 * predicates, date ops, …) falls back to the SERVER's verdict for that element — never a guess.
 * The op set below mirrors ConditionHelper.evaluateLeaf, including `valueField` (compare against
 * another fact) and `factor` (scale the compare operand — the 20%-of-cash-price rules).
 */
export interface ConditionRule {
  field?: string;
  op?: string;
  value?: unknown;
  /** compare against another fact instead of the static `value` */
  valueField?: string;
  /** scale the numeric compare operand (e.g. 0.2 ⇒ "20% of Cash Price") */
  factor?: number;
  all?: ConditionRule[];
  any?: ConditionRule[];
  not?: ConditionRule;
}

export interface FieldPrecondition {
  rule?: ConditionRule;
  whenUnmet?: "hidden" | "locked" | "blocked" | string;
}

type GateState = "visible" | "hidden" | "locked" | "blocked";
const EFFECT_PRECEDENCE: Record<string, number> = { locked: 1, blocked: 2, hidden: 3 };

const SUPPORTED_OPS = new Set([
  "exists",
  "not_exists",
  "equals",
  "not_equals",
  "in",
  "gt",
  "gte",
  "lt",
  "lte",
  "in_range",
]);

const present = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

/** mirrors ConditionHelper.normalize: case/whitespace-insensitive string identity */
const normalize = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const toNumber = (value: unknown): number =>
  typeof value === "number" ? value : parseFloat(String(value ?? ""));

/** true ⇒ every leaf of this rule uses an op the mirror implements (else: server verdict). */
export const ruleSupported = (rule: ConditionRule | undefined): boolean => {
  if (!rule || typeof rule !== "object") return true;
  if (Array.isArray(rule.all)) return rule.all.every(ruleSupported);
  if (Array.isArray(rule.any)) return rule.any.every(ruleSupported);
  if (rule.not) return ruleSupported(rule.not);
  if (!rule.field || !rule.op) return true;
  return SUPPORTED_OPS.has(rule.op);
};

export const evaluateRule = (
  rule: ConditionRule | undefined,
  context: Record<string, unknown>
): boolean => {
  if (!rule || typeof rule !== "object") return true;
  if (Array.isArray(rule.all)) return rule.all.every((child) => evaluateRule(child, context));
  if (Array.isArray(rule.any)) return rule.any.some((child) => evaluateRule(child, context));
  if (rule.not) return !evaluateRule(rule.not, context);
  if (!rule.field || !rule.op) return true;
  const actual = context[rule.field];
  const compare = rule.valueField !== undefined ? context[rule.valueField] : rule.value;
  const scaledCompare = toNumber(compare) * (typeof rule.factor === "number" ? rule.factor : 1);
  switch (rule.op) {
    case "exists":
      return present(actual);
    case "not_exists":
      return !present(actual);
    case "equals":
      return normalize(actual) === normalize(compare);
    case "not_equals":
      return normalize(actual) !== normalize(compare);
    case "in":
      return Array.isArray(rule.value) && rule.value.map(normalize).includes(normalize(actual));
    case "gt":
      return toNumber(actual) > scaledCompare;
    case "gte":
      return toNumber(actual) >= scaledCompare;
    case "lt":
      return toNumber(actual) < scaledCompare;
    case "lte":
      return toNumber(actual) <= scaledCompare;
    case "in_range": {
      if (!Array.isArray(rule.value) || rule.value.length !== 2) return false;
      const n = toNumber(actual);
      if (Number.isNaN(n)) return false;
      return n >= toNumber(rule.value[0]) && n <= toNumber(rule.value[1]);
    }
    default:
      return true; // fail-open — unreachable behind ruleSupported, kept for direct callers
  }
};

/** parse a precondition (single {rule, whenUnmet} or combined {clauses: [...]}) into its clauses. */
const clausesOf = (precondition: unknown): FieldPrecondition[] => {
  if (!precondition || typeof precondition !== "object") return [];
  const combined = (precondition as { clauses?: unknown }).clauses;
  if (Array.isArray(combined)) {
    return combined.filter(
      (clause): clause is FieldPrecondition => Boolean(clause) && typeof clause === "object"
    );
  }
  return [precondition as FieldPrecondition];
};

/** true ⇒ every clause rule of this precondition is locally judgeable. */
export const preconditionSupported = (precondition: unknown): boolean =>
  clausesOf(precondition).every((clause) => ruleSupported(clause.rule));

/** mirror of PreconditionHelper.evaluateState: no precondition / passing rules ⇒ visible; a failing
 * clause contributes its whenUnmet, strongest effect wins (locked < blocked < hidden). */
export const evaluatePreconditionState = (
  precondition: unknown,
  context: Record<string, unknown>
): GateState => {
  return clausesOf(precondition).reduce<GateState>((state, clause) => {
    if (evaluateRule(clause.rule, context)) return state;
    const effect = (clause.whenUnmet ?? "hidden") as GateState;
    if (state === "visible" || (EFFECT_PRECEDENCE[effect] ?? 0) > (EFFECT_PRECEDENCE[state] ?? 0)) {
      return effect;
    }
    return state;
  }, "visible");
};

/** the server-evaluated sets, used as the per-element fallback for unsupported rules. */
export interface ServerGates {
  hiddenFieldKeys: string[];
  lockedFieldKeys: string[];
  hiddenSectionNames: string[];
  hiddenOptionValues: Record<string, string[]>;
}

export interface LiveGates {
  hiddenFields: Set<string>;
  lockedFields: Set<string>;
  hiddenSections: Set<string>;
  hiddenOptions: Map<string, Set<string>>;
}

/** EXACT mirror of FormSchemaHelper.fieldKey — a placement is keyed by its REF, and an upstream
 * mirror by its qualified key. Diverging here collapses every label-less placement onto one key
 * ("Section.undefined") and the §Dual-placement union then keeps them ALL visible (live bite,
 * 2026-08-05: seller-nid + seller-cr rendered regardless of Seller Type). */
const fieldKeyOf = (section: FormSection, field: FormField): string => {
  const from = (field as { from?: { sourceStepOrder: number; fieldKey: string } }).from;
  if (from) return `@s${from.sourceStepOrder}:${from.fieldKey}`;
  if (field.ref) return field.ref;
  return field.k ?? `${section.name}.${field.l}`;
};

/**
 * mirror of PreconditionHelper.fieldGates + FormSchemaHelper.hiddenOptionValues, evaluated live —
 * including the §Dual-placement fold (a key is hidden only when EVERY placement hides it, locked
 * only when NO placement is writable). Elements with unsupported rules adopt the server verdict.
 */
export const computeLiveGates = (
  schema: FormSection[],
  context: Record<string, unknown>,
  server: ServerGates
): LiveGates => {
  const serverHiddenFields = new Set(server.hiddenFieldKeys);
  const serverLockedFields = new Set(server.lockedFieldKeys);
  const serverHiddenSections = new Set(server.hiddenSectionNames);

  const hidden = new Set<string>();
  const locked = new Set<string>();
  const visible = new Set<string>();
  const writable = new Set<string>();
  const hiddenSections = new Set<string>();
  const hiddenOptions = new Map<string, Set<string>>();

  for (const section of schema) {
    const sectionHidden = preconditionSupported(section.precondition)
      ? evaluatePreconditionState(section.precondition, context) === "hidden"
      : serverHiddenSections.has(section.name);
    if (sectionHidden) hiddenSections.add(section.name);

    for (const field of section.fields ?? []) {
      const key = fieldKeyOf(section, field);
      if (sectionHidden) {
        hidden.add(key);
        continue;
      }
      const state = preconditionSupported(field.precondition)
        ? evaluatePreconditionState(field.precondition, context)
        : serverHiddenFields.has(key)
          ? "hidden"
          : serverLockedFields.has(key)
            ? "locked"
            : "visible";
      if (state === "hidden") {
        hidden.add(key);
        continue;
      }
      visible.add(key);
      if (state === "locked") {
        locked.add(key);
      } else {
        writable.add(key);
      }

      // §Option-visibility: the server's verdict is the BASELINE, and the live optWhen rules only
      // refine it.
      //
      // This used to start from an empty set and populate it only from `optWhen`, so a field that
      // authored no `optWhen` — or an option the `optWhen` map does not mention — silently dropped
      // `hiddenOptionValues` entirely. Since `hiddenOptionsFor` reads ONLY these live gates when
      // they exist, a server-hidden option came back to life: the Resolution Path offered
      // "guarantor" on a case whose credit reviewer never offered it.
      const optWhen = (field as { optWhen?: Record<string, ConditionRule> }).optWhen;
      const hiddenValues = new Set<string>(server.hiddenOptionValues[key] ?? []);
      if (optWhen && (field.t === "radio" || field.t === "select")) {
        for (const [option, rule] of Object.entries(optWhen)) {
          // A rule this client CAN evaluate is live — it may hide an option or bring one back as
          // the agent types. One it cannot evaluate leaves the server's answer standing.
          if (!ruleSupported(rule)) continue;
          if (evaluateRule(rule, context)) hiddenValues.delete(option);
          else hiddenValues.add(option);
        }
      }
      if (hiddenValues.size) hiddenOptions.set(key, hiddenValues);
    }
  }

  return {
    hiddenFields: new Set([...hidden].filter((key) => !visible.has(key))),
    lockedFields: new Set([...locked].filter((key) => !writable.has(key))),
    hiddenSections,
    hiddenOptions,
  };
};

/** true ⇒ the field's own precondition (whenUnmet: hidden) is unmet against this draft. */
export const fieldHiddenByRule = (
  field: FormField,
  context: Record<string, unknown>
): boolean => {
  const precondition = field.precondition as FieldPrecondition | undefined;
  if (!precondition || typeof precondition !== "object") return false;
  if ((precondition.whenUnmet ?? "hidden") !== "hidden") return false;
  return !evaluateRule(precondition.rule, context);
};

/** the section's own precondition, same semantics as the field gate. */
export const sectionHiddenByRule = (
  section: FormSection,
  context: Record<string, unknown>
): boolean => {
  const precondition = (section as { precondition?: FieldPrecondition }).precondition;
  if (!precondition || typeof precondition !== "object") return false;
  if ((precondition.whenUnmet ?? "hidden") !== "hidden") return false;
  return !evaluateRule(precondition.rule, context);
};

/**
 * §Handoff-comment: does firing this action owe a comment BEFORE it is sent?
 *
 * The engine (`workflow-step-lifecycle.service.ts`) decides it three ways, and this client only
 * mirrored the first two:
 *   1. the action's own `requiresComment`;
 *   2. `step.mandatoryComments`, but ONLY on a REJECTED outcome;
 *   3. a REOPEN effect whose author flagged `requiresComment` — `EffectHelper.requiresHandoffComment`.
 *
 * (3) failed in the worst way: nothing asked, the action fired bare, and the agent got
 * "A comment is required for this action" having never been offered a box.
 *
 * `terminal` picks the engine's own split: a COMPLETION runs
 * `autoFired(action.effects + step.effectsOnComplete)` — external triggers do not fire there — while
 * a NON-terminal invoke runs the action's RAW effects, since pressing the button IS the trigger.
 */
const HANDOFF_EFFECT_TYPES = new Set(["TIME_TRAVEL", "CONDITIONAL_TIME_TRAVEL", "REOPEN_SUBFLOW"]);

const demandsComment = (effect: unknown): boolean => {
  if (typeof effect !== "object" || effect === null) return false;
  const e = effect as {type?: unknown; requiresComment?: unknown};
  return e.requiresComment === true && HANDOFF_EFFECT_TYPES.has(String(e.type));
};

const autoFired = (effects: unknown[]): unknown[] =>
  effects.filter((effect) => {
    if (typeof effect !== "object" || effect === null) return true;
    return (effect as {trigger?: unknown}).trigger !== "external";
  });

export const actionNeedsComment = ({
  actionRequiresComment,
  isRejectOutcome,
  mandatoryComments,
  actionEffects = [],
  stepEffectsOnComplete = [],
  terminal,
}: {
  actionRequiresComment?: boolean;
  isRejectOutcome: boolean;
  mandatoryComments?: boolean;
  actionEffects?: unknown[];
  stepEffectsOnComplete?: unknown[];
  terminal: boolean;
}): boolean => {
  if (actionRequiresComment) return true;
  if (isRejectOutcome && mandatoryComments) return true;
  const effects = terminal
    ? autoFired([...actionEffects, ...stepEffectsOnComplete])
    : actionEffects;
  return effects.some(demandsComment);
};

/**
 * A keyed action without an outcome is a non-terminal command. It belongs on `invoke-action`
 * even when it has no authored effects: the action handler itself may be the behavior, and sending
 * it through step completion would silently turn it into the fallback APPROVED outcome.
 */
export const actionUsesInvokeEndpoint = (action: {
  key: string | null;
  outcome?: string;
}): action is {key: string; outcome?: never} => Boolean(action.key) && !action.outcome;
